import { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Skeleton } from "@mui/material";
import { FormInput } from "@/components/ui/forms/formInputComponent/formInput.component";
import { useVerifySpaceUsernameExists } from "@/api/space/hooks/useSpace.2factor";
import type { ISpaceOtpUserLookupRequest } from "@/api/space/dtos/2factor.dto";
import { obterErroApi } from "@/utils/errors/apiError";
import {
	esquemaOtpAutenticacaoDoisFatores,
	type EsquemaOtpAutenticacaoDoisFatores,
} from "./autenticacaoDoisFatores.schema";
import { isSignedInteger } from "@/utils/utils";

// --------------------------------- TYPES ---------------------------------
type CampoUsuarioSpaceOtpProps = {
	readonly aberto: boolean;
	readonly permitirBuscaUsuarioPorCodigo: boolean;
	readonly groupId?: string;
	readonly slug?: string;
	readonly rotulo: string;
	readonly placeholder: string;
	readonly carregando: boolean;
	readonly aoValidar: (nome: string | null) => void;
	readonly consulta?: {
		readonly contexto: "sessao" | "login";
		readonly habilitada: boolean;
		readonly aoErro: (error: unknown) => void;
	};
};

// -------------------------------- HELPERS --------------------------------
function montarIdentificadorUsuarioSpace(
	identificador: string,
	permitirBuscaUsuarioPorCodigo: boolean,
): ISpaceOtpUserLookupRequest {
	if (permitirBuscaUsuarioPorCodigo && isSignedInteger(identificador) && Number(identificador) > 0)
		return { id: Number(identificador) };
	return { nome_usuario: identificador };
}

// ------------------------------- COMPONENT -------------------------------
export default function CampoUsuarioSpaceOtp({
	aberto,
	permitirBuscaUsuarioPorCodigo,
	groupId,
	slug,
	rotulo,
	placeholder,
	carregando,
	aoValidar,
	consulta,
}: Readonly<CampoUsuarioSpaceOtpProps>) {
	// -------------------------------- STATES --------------------------------
	const [consultando, setConsultando] = useState(false);
	const nomeAtualRef = useRef("");
	const resolvidoRef = useRef<{
		termo: string;
		nome: string;
		groupId: string | undefined;
		slug: string | undefined;
	} | null>(null);
	const contexto = consulta?.contexto ?? "sessao";
	const habilitada = consulta?.habilitada ?? true;
	const aoErro = consulta?.aoErro;

	// --------------------------------- FORM ---------------------------------
	const { control, reset, setValue, setError, clearErrors } = useForm<EsquemaOtpAutenticacaoDoisFatores>({
		mode: "onSubmit",
		resolver: zodResolver(esquemaOtpAutenticacaoDoisFatores),
		defaultValues: { nomeUsuario: "" },
	});
	const nome = useWatch({ control, name: "nomeUsuario" }).trim().toUpperCase();
	nomeAtualRef.current = nome;

	// -------------------------------- QUERIES -------------------------------
	const { mutateAsync: verificarUsuario } = useVerifySpaceUsernameExists(contexto);

	// -------------------------------- EFFECTS -------------------------------
	useEffect(() => {
		resolvidoRef.current = null;
		reset({ nomeUsuario: "" });
	}, [aberto, reset]);

	useEffect(() => {
		const resolvido = resolvidoRef.current;

		if (
			aberto &&
			habilitada &&
			resolvido?.groupId === groupId &&
			resolvido?.slug === slug &&
			resolvido?.termo === nome
		) {
			aoValidar(resolvido.nome);
			return;
		}

		resolvidoRef.current = null;
		aoValidar(null);
		clearErrors("nomeUsuario");
		setConsultando(false);

		if (!aberto || !habilitada || !nome || (contexto === "login" && !groupId && !slug)) return;

		const controller = new AbortController();

		async function consultarUsuario(): Promise<void> {
			setConsultando(true);
			try {
				const usuario = await verificarUsuario({
					...montarIdentificadorUsuarioSpace(nome, permitirBuscaUsuarioPorCodigo),
					groupId,
					slug,
					signal: controller.signal,
				});

				if (controller.signal.aborted || nomeAtualRef.current !== nome) return;

				if (usuario?.ativo !== true || typeof usuario.nome !== "string" || !usuario.nome.trim()) {
					setError("nomeUsuario", { type: "server", message: "Usuário não encontrado ou inativo" });
					return;
				}

				const nomeResolvido = usuario.nome.trim().toUpperCase();
				const substituirCodigo = permitirBuscaUsuarioPorCodigo && isSignedInteger(nome);

				resolvidoRef.current = {
					termo: substituirCodigo ? nomeResolvido : nome,
					nome: nomeResolvido,
					groupId,
					slug,
				};

				if (substituirCodigo) setValue("nomeUsuario", nomeResolvido, { shouldDirty: true });

				aoValidar(nomeResolvido);
			} catch (error: unknown) {
				if (controller.signal.aborted || nomeAtualRef.current !== nome) return;

				aoErro?.(error);
				setError("nomeUsuario", {
					type: "server",
					message: obterErroApi(error)?.message ?? "Não foi possível validar o usuário. Tente novamente.",
				});
			} finally {
				if (!controller.signal.aborted) setConsultando(false);
			}
		}

		const timer = globalThis.setTimeout(() => {
			void consultarUsuario();
		}, 450);

		return () => {
			globalThis.clearTimeout(timer);
			controller.abort();
		};
	}, [
		aberto,
		habilitada,
		contexto,
		nome,
		groupId,
		slug,
		permitirBuscaUsuarioPorCodigo,
		verificarUsuario,
		aoValidar,
		aoErro,
		clearErrors,
		setError,
		setValue,
	]);

	// -------------------------------- RETURN --------------------------------
	return (
		<div aria-busy={consultando}>
			<FormInput<EsquemaOtpAutenticacaoDoisFatores>
				control={control}
				name="nomeUsuario"
				id={contexto === "login" ? "nome_usuario" : undefined}
				label={rotulo}
				placeholder={placeholder}
				autoFocus={contexto === "sessao" || Boolean(groupId || slug)}
				disabled={carregando}
				parse={(valor) => valor.toUpperCase()}
				fullWidth
				className="mx-auto"
				size="medium"
				reserveHelperText={false}
			/>
			{consultando ? <Skeleton height={4} width="100%" /> : null}
		</div>
	);
}
